import { useContext } from "react";
import WebsocketWrapperContext from "../../../context/WebsocketWrapperContext";
import { useBuilderContext } from "../context/BuilderContext";
import { format } from "date-fns";
import { toast } from "react-toastify";

import ErrorIcon from "../../../images/error_icon.svg";

export const useApplyPreBuiltStrategy = () => {
  const {
    allSymbols,
    selectedSymbol,
    allOptionsData,
    currentOptionsData,
    selectedExpiry,
    getFirstFutureInstrument,
    normalizeExpiryDate,
    currentFuturesData,
  } = useBuilderContext();

  const {
    checkWebsocketConnection,
    subscribeToSymbols,
    unsubscribeToSymbols,
    symbolList,
    symbolData,
  } = useContext(WebsocketWrapperContext);

  // Add to useApplyPreBuiltStrategy.js
  function applyHedgeStrategy(strategyKey) {
    const atmIndex = returnAtmIndex();
    let childData = [];

    // Check if we have futures data available for hedge strategies
    if (!currentFuturesData) {
      console.warn('No futures data available for hedge strategies');
      return [];
    }

    const options = currentOptionsData[selectedExpiry]?.options || [];
    if (options.length === 0) {
      console.warn('No options data available for hedge strategies');
      return [];
    }

    // Helper function to get valid strike index with fallback logic
    const getValidStrikeIndex = (preferredOffset, fallbackOffsets = []) => {
      const tryIndex = atmIndex + preferredOffset;
      if (tryIndex >= 0 && tryIndex < options.length) {
        return tryIndex;
      }

      // Try fallback offsets in order
      for (const offset of fallbackOffsets) {
        const fallbackIndex = atmIndex + offset;
        if (fallbackIndex >= 0 && fallbackIndex < options.length) {
          return fallbackIndex;
        }
      }

      return null;
    };

    switch (strategyKey) {
      // Bullish Strategies
      case "bullish_recommended": {
        const fiveStrikesBehind = getValidStrikeIndex(-5, [-4, -3, -2, -1]);
        if (fiveStrikesBehind === null) {
          console.warn('Cannot create bullish_recommended: no valid strike found');
          return [];
        }
        childData = [
          constructFutureInstrument(currentFuturesData, "Buy", 1),
          constructOptionInstrument("Put", fiveStrikesBehind, "Buy", 1),
        ].filter(Boolean);
        break;
      }

      case "bullish_low_margin": {
        childData = [
          constructFutureInstrument(currentFuturesData, "Buy", 1),
          constructOptionInstrument("Put", atmIndex, "Buy", 1),
        ].filter(Boolean);
        break;
      }

      case "bullish_low_hedge_cost": {
        const fiveStrikesBehind = getValidStrikeIndex(-5, [-4, -3, -2, -1]);
        const nineteenStrikesBehind = getValidStrikeIndex(-19, [-15, -10, -8]) ?? fiveStrikesBehind;

        if (nineteenStrikesBehind === null) {
          console.warn('Cannot create bullish_low_hedge_cost: no valid strike found');
          return [];
        }

        childData = [
          constructFutureInstrument(currentFuturesData, "Buy", 1),
          constructOptionInstrument("Put", nineteenStrikesBehind, "Buy", 1),
        ].filter(Boolean);
        break;
      }

      // Bearish Strategies
      case "bearish_recommended": {
        const fourStrikesAhead = getValidStrikeIndex(4, [3, 2, 1]);
        if (fourStrikesAhead === null) {
          console.warn('Cannot create bearish_recommended: no valid strike found');
          return [];
        }
        childData = [
          constructFutureInstrument(currentFuturesData, "Sell", 1),
          constructOptionInstrument("Call", fourStrikesAhead, "Buy", 1),
        ].filter(Boolean);
        break;
      }

      case "bearish_low_margin": {
        childData = [
          constructFutureInstrument(currentFuturesData, "Sell", 1),
          constructOptionInstrument("Call", atmIndex, "Buy", 1),
        ].filter(Boolean);
        break;
      }

      case "bearish_low_hedge_cost": {
        const fourStrikesAhead = getValidStrikeIndex(4, [3, 2, 1]);
        const nineteenStrikesAhead = getValidStrikeIndex(19, [15, 10, 8]) ?? fourStrikesAhead;

        if (nineteenStrikesAhead === null) {
          console.warn('Cannot create bearish_low_hedge_cost: no valid strike found');
          return [];
        }

        childData = [
          constructFutureInstrument(currentFuturesData, "Sell", 1),
          constructOptionInstrument("Call", nineteenStrikesAhead, "Buy", 1),
        ].filter(Boolean);
        break;
      }

      // Neutral Strategies
      case "neutral_recommended": {
        const twoStrikesAhead = getValidStrikeIndex(2, [1]);
        const oneStrikeBehind = getValidStrikeIndex(-1, [-2]);

        if (twoStrikesAhead === null || oneStrikeBehind === null) {
          console.warn('Cannot create neutral_recommended: no valid strikes found');
          return [];
        }

        childData = [
          constructOptionInstrument("Call", twoStrikesAhead, "Sell", 1),
          constructOptionInstrument("Put", oneStrikeBehind, "Sell", 1),
        ].filter(Boolean);
        break;
      }

      case "neutral_low_margin": {
        childData = [
          constructOptionInstrument("Call", atmIndex, "Sell", 1),
          constructOptionInstrument("Put", atmIndex, "Sell", 1),
        ].filter(Boolean);
        break;
      }

      case "neutral_low_hedge_cost": {
        const fourStrikesAhead = getValidStrikeIndex(4, [3, 2, 1]);
        const fourStrikesBehind = getValidStrikeIndex(-4, [-3, -2, -1]);

        if (fourStrikesAhead === null || fourStrikesBehind === null) {
          console.warn('Cannot create neutral_low_hedge_cost: no valid strikes found');
          return [];
        }

        childData = [
          constructOptionInstrument("Call", fourStrikesAhead, "Sell", 1),
          constructOptionInstrument("Put", fourStrikesBehind, "Sell", 1),
        ].filter(Boolean);
        break;
      }

      default:
        console.warn(`Unknown hedge strategy: ${strategyKey}`);
    }

    return childData;
  }

  // Helper function to create future instrument
  function constructFutureInstrument(currentFuturesData, action, quantity) {
    if (!currentFuturesData) {
      return null;
    }
    return {
      buy_sell: action,
      code: currentFuturesData.code,
      expiryDate: currentFuturesData.expiry_date,
      instrumentName: currentFuturesData.instrument_name,
      lotSize: currentFuturesData.lot_size,
      side: "Future",
      tradingSymbol: currentFuturesData.trading_symbol,
      quantity: quantity,
      strike: "FUTURE",
      freeze_qty: currentFuturesData.freeze_qty,
      exchange: currentFuturesData.exchange,
      lot_multiplier: parseFloat(currentFuturesData.lot_size),
      isFuture: true,
    };
  }

  // Helper function to create option instrument
  function constructOptionInstrument(side, indexOffset, action, quantity) {
    let atmIndex = indexOffset;

    // Safety checks
    if (atmIndex < 0) atmIndex = 0;
    const options = currentOptionsData[selectedExpiry]?.options || [];
    if (atmIndex >= options.length) atmIndex = options.length - 1;

    const option =
      side === "Call" ? options[atmIndex]?.call : options[atmIndex]?.put;

    if (!option) return null;

    return {
      buy_sell: action,
      code: option.code,
      expiryDate: option.expiry_date,
      instrumentName: option.instrument_name,
      lotSize: option.lot_size,
      side: side,
      tradingSymbol: option.trading_symbol,
      quantity: quantity,
      strike: option.strike,
      freeze_qty: option.freeze_qty,
      exchange: option.exchange,
      lot_multiplier: parseFloat(option.lot_size),
      isFuture: false,
    };
  }

  // Helper function to find the last expiry of a specific month
  // Used when applying saved strategies with CW, CM, CM+1, CM+2 expiries
  function getLastExpiryOfMonth(monthOffset = 0) {
    if (!selectedSymbol || !selectedSymbol.option_expiries) {
      return null;
    }

    const currentDate = new Date();
    const targetMonth = (currentDate.getMonth() + monthOffset) % 12;
    const targetYear =
      currentDate.getFullYear() +
      Math.floor((currentDate.getMonth() + monthOffset) / 12);

    // Get all expiries as Date objects
    const allExpiries = selectedSymbol.option_expiries.map(
      (exp) => new Date(exp)
    );

    // Filter expiries for target month
    const targetMonthExpiries = allExpiries.filter(
      (date) =>
        date.getMonth() === targetMonth && date.getFullYear() === targetYear
    );


    // Find the last expiry of the target month
    if (targetMonthExpiries.length > 0) {
      const lastExpiry = new Date(
        Math.max(...targetMonthExpiries.map((d) => d.getTime()))
      );

      // Find the index of this expiry in the original array
      const expiryIndex = selectedSymbol.option_expiries.findIndex(
        (exp) => new Date(exp).getTime() === lastExpiry.getTime()
      );

      return expiryIndex >= 0 ? expiryIndex : null;
    }

    return null;
  }

  // Helper function to find the last expiry of the current month
  // Used when applying saved strategies with CW (Current Week) expiry
  function getLastExpiryOfCurrentMonth() {
    return getLastExpiryOfMonth(0);
  }

  // Helper function to check if a strategy has weekly expiries
  function hasWeeklyExpiries(expiryIndexes) {
    if (!expiryIndexes || expiryIndexes.length === 0) return false;

    // Weekly expiries are typically the first few expiries (CW, CW+1, CW+2, etc.)
    // We'll consider the first 4 expiries as weekly expiries
    return expiryIndexes.some((index) => index < 4);
  }

  // Helper function to check if current instrument has weekly expiries
  function hasWeeklyExpiriesAvailable() {
    if (
      !selectedSymbol ||
      !selectedSymbol.option_expiries ||
      selectedSymbol.option_expiries.length < 2
    ) {
      return false;
    }

    const currentDate = new Date();
    const sortedExpiries = selectedSymbol.option_expiries;

    // If we have less than 2 future expiries, we can't determine weekly availability
    if (sortedExpiries.length < 2) {
      return false;
    }

    // Check gaps between consecutive expiries
    for (let i = 0; i < sortedExpiries.length - 1; i++) {
      const currentExpiry = sortedExpiries[i];
      const nextExpiry = sortedExpiries[i + 1];

      const diffDays = Math.ceil(
        (new Date(nextExpiry) - new Date(currentExpiry)) / (1000 * 60 * 60 * 24)
      );
      // If gap between consecutive expiries is more than 7 days,
      // it means weekly expiries are not available
      if (diffDays <= 10) {
        return true;
      }
      return false;
    }

    // If all gaps are 7 days or less, weekly expiries are available
    return true;
  }

  // Helper function to map saved strategy expiry indices to actual expiry indices
  function mapSavedExpiryIndexToActualExpiry(savedIndex) {
    if (!selectedSymbol || !selectedSymbol.option_expiries) {
      console.warn("mapSavedExpiryIndexToActualExpiry: No selectedSymbol or option_expiries");
      return 0;
    }

    const availableExpiries = selectedSymbol.option_expiries;
    const currentDate = new Date();

    console.log(`Mapping saved index ${savedIndex} to actual expiry. Available expiries:`, availableExpiries);

    // Map saved strategy indices to actual expiry logic
    let actualIndex;
    switch (savedIndex) {
      case 0: // CW - Current Week
        actualIndex = 0; // Map to first available expiry
        console.log(`CW (index 0) mapped to actual index: ${actualIndex}`);
        break;

      case 1: // CW + 1
        actualIndex = Math.min(1, availableExpiries.length - 1);
        console.log(`CW + 1 (index 1) mapped to actual index: ${actualIndex}`);
        break;

      case 2: // CW + 2
        actualIndex = Math.min(2, availableExpiries.length - 1);
        console.log(`CW + 2 (index 2) mapped to actual index: ${actualIndex}`);
        break;

      case 3: // CW + 3
        actualIndex = Math.min(3, availableExpiries.length - 1);
        console.log(`CW + 3 (index 3) mapped to actual index: ${actualIndex}`);
        break;

      case 4: // CM - Current Month
        actualIndex = getLastExpiryOfCurrentMonth() || 0;
        console.log(`CM (index 4) mapped to actual index: ${actualIndex}`);
        break;

      case 5: // CW + 4
        actualIndex = Math.min(4, availableExpiries.length - 1);
        console.log(`CW + 4 (index 5) mapped to actual index: ${actualIndex}`);
        break;

      case 6: // CW + 5
        actualIndex = Math.min(5, availableExpiries.length - 1);
        console.log(`CW + 5 (index 6) mapped to actual index: ${actualIndex}`);
        break;

      case 7: // CW + 6
        actualIndex = Math.min(6, availableExpiries.length - 1);
        console.log(`CW + 6 (index 7) mapped to actual index: ${actualIndex}`);
        break;

      case 8: // CM + 1
        actualIndex = getLastExpiryOfMonth(1) || Math.min(7, availableExpiries.length - 1);
        console.log(`CM + 1 (index 8) mapped to actual index: ${actualIndex}`);
        break;

      case 9: // CW + 7
        actualIndex = Math.min(7, availableExpiries.length - 1);
        console.log(`CW + 7 (index 9) mapped to actual index: ${actualIndex}`);
        break;

      case 10: // CW + 8
        actualIndex = Math.min(8, availableExpiries.length - 1);
        console.log(`CW + 8 (index 10) mapped to actual index: ${actualIndex}`);
        break;

      case 11: // CW + 9
        actualIndex = Math.min(9, availableExpiries.length - 1);
        console.log(`CW + 9 (index 11) mapped to actual index: ${actualIndex}`);
        break;

      case 12: // CM + 2
        actualIndex = getLastExpiryOfMonth(2) || Math.min(10, availableExpiries.length - 1);
        console.log(`CM + 2 (index 12) mapped to actual index: ${actualIndex}`);
        break;

      case 13: // CM + 3
        actualIndex = getLastExpiryOfMonth(3) || Math.min(11, availableExpiries.length - 1);
        console.log(`CM + 3 (index 13) mapped to actual index: ${actualIndex}`);
        break;

      default:
        actualIndex = Math.min(savedIndex, availableExpiries.length - 1);
        console.log(`Default case (index ${savedIndex}) mapped to actual index: ${actualIndex}`);
        break;
    }

    console.log(`Final mapping: saved index ${savedIndex} -> actual index ${actualIndex} -> expiry date: ${availableExpiries[actualIndex]}`);
    return actualIndex;
  }

  function applySavedStrategy(savedStrategy) {
    if (!savedStrategy || !savedStrategy.strategy) {
      return;
    }

    // Log current month's last expiry for debugging
    const lastExpiryOfCurrentMonth = getLastExpiryOfCurrentMonth();
    if (lastExpiryOfCurrentMonth !== null) {
      const lastExpiryDate =
        selectedSymbol.option_expiries[lastExpiryOfCurrentMonth];
    }

    if (!currentOptionsData || !currentOptionsData[selectedExpiry]?.options) {
      return;
    }


    const strategy = savedStrategy.strategy;

    // Extract data from saved strategy
    const callPutList = strategy.call_put_list || [];
    const buySellList = strategy.buy_sell_list || [];
    const lotsList = strategy.lots_list || [];
    const atmIndexList = strategy.atmindex_list || [];
    const expiryIndexes = strategy.expiries || [];
    const expiryDates = strategy.expiry_dates || [];

    // Check if strategy has weekly expiries and if current instrument supports them
    if (hasWeeklyExpiries(expiryIndexes) && !hasWeeklyExpiriesAvailable()) {
      toast.error("Weekly expiries are not available for this instrument", {
        position: "bottom-center",
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        className: "custom-snackbar",
        bodyClassName: "custom-snackbar-body",
      });
      return null;
    }
    // Calculate actual indices based on ATM index
    console.log("applySavedStrategy: Processing strategy with expiryIndexes:", expiryIndexes);
    console.log("applySavedStrategy: Available option expiries:", selectedSymbol.option_expiries);

    const actualIndices = atmIndexList.map((offset, index) => {
      const isPut = callPutList[index] === "Put";
      let expiry = "";

      if (callPutList[index] !== "Fut") {
        // Map the saved strategy expiry index to actual expiry date
        const expiryIndex = mapSavedExpiryIndexToActualExpiry(expiryIndexes[index]);
        expiry = selectedSymbol.option_expiries[expiryIndex];
        console.log(`Leg ${index}: Saved expiry index ${expiryIndexes[index]} -> actual index ${expiryIndex} -> expiry date ${expiry}`);
      } else {
        expiry = selectedSymbol.fut_expiries[expiryIndexes[index]];
        console.log(`Leg ${index}: Future expiry index ${expiryIndexes[index]} -> expiry date ${expiry}`);
      }

      const atmIndex = returnExpiryAtmIndex(expiry);
      const finalIndex = atmIndex + (isPut ? -1 * offset : offset);
      console.log(`Leg ${index}: ATM index ${atmIndex}, offset ${offset}, final index ${finalIndex}`);
      return finalIndex;
    });

    // Check if strategy has multiple expiries (like calendar spreads)
    const hasMultipleExpiries =
      (expiryIndexes && expiryIndexes.length > 0 && expiryIndexes.some((index) => index !== expiryIndexes[0])) ||
      (expiryDates && expiryDates.length > 0 && expiryDates.some((d) => d !== expiryDates[0]));

    let childData = [];
    // debugger;
    childData = constructMultiExpiryInstruments(
      callPutList,
      buySellList,
      actualIndices,
      lotsList,
      expiryIndexes,
      expiryDates
    );
    return childData;
  }

  // New function to handle multi-expiry strategies
  // For CW (Current Week) strategies, automatically selects the last expiry of the current month
  // For CM (Current Month) strategies, automatically selects the last expiry of the current month
  // For CM+1 and CM+2 strategies, automatically selects the last expiry of their respective months
  // This prevents conflicts where CW+1 and CM might refer to the same expiry date
  function constructMultiExpiryInstruments(
    call_put_array,
    buy_sell_array,
    index_array,
    quantity_array,
    expiry_indexes,
    expiry_dates
  ) {
    let row_list = [];
    const availableExpiries = selectedSymbol.option_expiries;

    for (let index = 0; index < call_put_array.length; index++) {
      // Check if this should be a future instrument
      if (call_put_array[index] === "Fut") {
        // Create future instrument
        const futureInstrument = constructFutureInstrument(
          currentFuturesData,
          buy_sell_array[index],
          quantity_array[index]
        );
        if (futureInstrument) {
          row_list.push(futureInstrument);
        }
        continue;
      }

      // Use the mapping function to get the correct expiry index
      let expiryIndex = mapSavedExpiryIndexToActualExpiry(expiry_indexes[index]);

      // Get expiry date based on index
      if (expiryIndex < 0) {
        expiryIndex = 0;
      } else if (expiryIndex >= availableExpiries.length) {
        // Fallback to first available expiry
        expiryIndex = 0;
      }

      const expiryDate = availableExpiries[expiryIndex];
      const normalizedExpiry = normalizeExpiryDate(expiryDate);


      // Get option data for this expiry
      const expiryData = currentOptionsData[expiryDate];
      if (!expiryData || !expiryData.options) {
        continue;
      }

      let atmIndex = index_array[index];

      // Safety check for index
      if (atmIndex < 0) atmIndex = 0;
      if (atmIndex >= expiryData.options.length) {
        atmIndex = expiryData.options.length - 1;
      }

      const side = call_put_array[index];
      const option =
        side === "Call"
          ? expiryData.options[atmIndex].call
          : expiryData.options[atmIndex].put;

      row_list.push({
        buy_sell: buy_sell_array[index],
        code: option.code,
        expiryDate: option.expiry_date,
        instrumentName: option.instrument_name,
        lotSize: option.lot_size,
        side: side,
        tradingSymbol: option.trading_symbol,
        quantity: quantity_array[index],
        strike: option.strike,
        freeze_qty: option.freeze_qty,
        exchange: option.exchange,
        lot_multiplier: parseFloat(option.lot_size),
        expiry: normalizedExpiry, // Add normalized expiry for basket component
        allData: expiryData.options, // Add all data for strike operations
        isCALL: side === "Call",
        isBuy: buy_sell_array[index] === "Buy",
        isFuture: false,
        orderType: "M", // Default to Market order
        price:
          symbolData[option.code]?.ltp ||
          symbolData[option.code]?.prev_close ||
          "", // Set initial price from symbolData
        allstrks: expiryData.options.map((opt) => opt.strike), // Add strike list
      });
    }

    return row_list;
  }

  function applyPreBuiltStrategy(strategyName) {
    const atmIndex = returnAtmIndex();
    let childData = [];
    // setSelectedStrategy(strategyName);

    if (!currentOptionsData || !currentOptionsData[selectedExpiry]?.options) {
      return;
    }

    switch (strategyName) {
      case "buy_future":
        childData = constructInstruments(["Fut"], ["Buy"], [0], [1]);
        break;
      case "sell_future":
        childData = constructInstruments(["Fut"], ["Sell"], [0], [1]);
        break;
      case "buy_call":
        childData = constructInstruments(["Call"], ["Buy"], [atmIndex], [1]);
        break;
      case "sell_put":
        childData = constructInstruments(["Put"], ["Sell"], [atmIndex], [1]);
        break;
      case "bull_call_spread":
        childData = constructInstruments(
          ["Call", "Call"],
          ["Buy", "Sell"],
          [atmIndex + 2, atmIndex + 5],
          [1, 1]
        );
        break;
      case "bull_put_spread":
        childData = constructInstruments(
          ["Put", "Put"],
          ["Buy", "Sell"],
          [atmIndex - 6, atmIndex - 1],
          [1, 1]
        );
        break;
      case "bull_butterfly":
        childData = constructInstruments(
          ["Call", "Call", "Call"],
          ["Buy", "Sell", "Buy"],
          [atmIndex + 5, atmIndex + 10, atmIndex + 15],
          [1, 2, 1]
        );
        break;
      case "bull_condor":
        childData = constructInstruments(
          ["Call", "Call", "Call", "Call"],
          ["Buy", "Sell", "Sell", "Buy"],
          [atmIndex + 5, atmIndex + 10, atmIndex + 15, atmIndex + 20],
          [1, 1, 1, 1]
        );
        break;
      case "call_ratio_back_spread":
        childData = constructInstruments(
          ["Call", "Call"],
          ["Buy", "Sell"],
          [atmIndex + 3, atmIndex - 2],
          [2, 1]
        );
        break;
      case "buy_put":
        childData = constructInstruments(["Put"], ["Buy"], [atmIndex], [1]);
        break;
      case "sell_call":
        childData = constructInstruments(["Call"], ["Sell"], [atmIndex], [1]);
        break;
      case "bear_put_spread":
        childData = constructInstruments(
          ["Put", "Put"],
          ["Sell", "Buy"],
          [atmIndex - 4, atmIndex],
          [1, 1]
        );
        break;
      case "bear_call_spread":
        childData = constructInstruments(
          ["Call", "Call"],
          ["Sell", "Buy"],
          [atmIndex, atmIndex + 4],
          [1, 1]
        );
        break;
      case "bear_butterfly":
        childData = constructInstruments(
          ["Put", "Put", "Put"],
          ["Buy", "Sell", "Buy"],
          [atmIndex - 5, atmIndex - 10, atmIndex - 15],
          [1, 2, 1]
        );
        break;
      case "bear_condor":
        childData = constructInstruments(
          ["Put", "Put", "Put", "Put"],
          ["Buy", "Sell", "Sell", "Buy"],
          [atmIndex - 5, atmIndex - 10, atmIndex - 15, atmIndex - 20],
          [1, 1, 1, 1]
        );
        break;
      case "put_ratio_back_spread":
        childData = constructInstruments(
          ["Put", "Put"],
          ["Buy", "Sell"],
          [atmIndex - 2, atmIndex + 3],
          [2, 1]
        );
        break;
      case "short_straddle":
        childData = constructInstruments(
          ["Call", "Put"],
          ["Sell", "Sell"],
          [atmIndex, atmIndex],
          [1, 1]
        );
        break;
      case "short_strangle":
        childData = constructInstruments(
          ["Call", "Put"],
          ["Sell", "Sell"],
          [atmIndex + 8, atmIndex - 8],
          [1, 1]
        );
        break;
      case "iron_butterfly":
        childData = constructInstruments(
          ["Call", "Put", "Call", "Put"],
          ["Buy", "Buy", "Sell", "Sell"],
          [atmIndex + 5, atmIndex - 5, atmIndex, atmIndex],
          [1, 1, 1, 1]
        );
        break;
      case "iron_condor":
        childData = constructInstruments(
          ["Call", "Put", "Call", "Put"],
          ["Buy", "Buy", "Sell", "Sell"],
          [atmIndex + 9, atmIndex - 9, atmIndex + 5, atmIndex - 5],
          [1, 1, 1, 1]
        );
        break;
      case "batman":
        childData = constructInstruments(
          ["Call", "Call", "Put", "Put"],
          ["Buy", "Sell", "Buy", "Sell"],
          [atmIndex + 5, atmIndex + 6, atmIndex - 5, atmIndex - 6],
          [1, 2, 1, 2]
        );
        break;
      case "jade_lizard":
        childData = constructInstruments(
          ["Call", "Call", "Put"],
          ["Sell", "Buy", "Sell"],
          [atmIndex + 9, atmIndex + 13, atmIndex - 5],
          [1, 1, 1]
        );
        break;
      case "double_plateau":
        childData = constructInstruments(
          ["Call", "Call", "Call", "Call", "Put", "Put", "Put", "Put"],
          ["Buy", "Sell", "Sell", "Buy", "Buy", "Sell", "Sell", "Buy"],
          [
            atmIndex + 5,
            atmIndex + 10,
            atmIndex + 15,
            atmIndex + 20,
            atmIndex - 4,
            atmIndex - 9,
            atmIndex - 14,
            atmIndex - 19,
          ],
          [1, 1, 1, 1, 1, 1, 1, 1]
        );
        break;
      case "reverse_jade_lizard":
        childData = constructInstruments(
          ["Put", "Put", "Call"],
          ["Sell", "Buy", "Sell"],
          [atmIndex - 9, atmIndex - 12, atmIndex + 5],
          [1, 1, 1]
        );
        break;
      case "long_calendar_calls":
        childData = handleCalendarStrategy("Call", atmIndex + 5);
        break;
      case "long_calendar_puts":
        childData = handleCalendarStrategy("Put", atmIndex - 4);
        break;
      default:
        return;
    }
    return childData;
    // handleCallback([childData]);
  }
  function handleCalendarStrategy(optionType, strikeIndex) {
    try {
      // Parse the current expiry date
      const currentDate = new Date(selectedExpiry);
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      // Get all expiries as Date objects
      const allExpiries = selectedSymbol.option_expiries.map(
        (exp) => new Date(exp)
      );

      // current month
      const currentMonthExpiries = allExpiries.filter(
        (date) =>
          date.getMonth() === currentMonth && date.getFullYear() === currentYear
      );

      // Find the last expiry of the current month
      let lastExpiryOfCurrentMonth = null;
      if (currentMonthExpiries.length > 0) {
        lastExpiryOfCurrentMonth = new Date(
          Math.max(...currentMonthExpiries.map((d) => d.getTime()))
        );
      }

      let nextExpiry;

      // Check if there's a suitable expiry later in the current month
      if (
        lastExpiryOfCurrentMonth &&
        (lastExpiryOfCurrentMonth.getTime() - currentDate.getTime()) /
        (1000 * 60 * 60 * 24) >=
        14
      ) {
        nextExpiry = lastExpiryOfCurrentMonth;
      } else {
        // Look for expiries in the next month
        const nextMonth = (currentMonth + 1) % 12;
        const nextYear = nextMonth === 0 ? currentYear + 1 : currentYear;

        const nextMonthExpiries = allExpiries.filter(
          (date) =>
            date.getMonth() === nextMonth && date.getFullYear() === nextYear
        );

        if (nextMonthExpiries.length > 0) {
          nextExpiry = new Date(
            Math.max(...nextMonthExpiries.map((d) => d.getTime()))
          );
        } else {
          throw new Error("No valid expiry dates found in the next month");
        }
      }
      // Find matching expiry keys in currentOptionsData
      const findMatchingExpiryKey = (targetDate) => {
        const targetTime = targetDate.getTime();
        for (const expiryKey of Object.keys(currentOptionsData)) {
          const expiryTime = new Date(expiryKey).getTime();
          if (Math.abs(targetTime - expiryTime) < 24 * 60 * 60 * 1000) { // Within 1 day
            return expiryKey;
          }
        }
        return null;
      };

      const currentExpiryKey = findMatchingExpiryKey(currentDate);
      const nextExpiryKey = findMatchingExpiryKey(nextExpiry);

      if (!currentExpiryKey || !nextExpiryKey) {
        return [];
      }

      return constructCalendarInstruments(
        [optionType, optionType],
        ["Sell", "Buy"],
        [strikeIndex, strikeIndex],
        [1, 1],
        [currentExpiryKey, nextExpiryKey]
      );
    } catch (error) {
      return [];
    }
  }
  function constructCalendarInstruments(
    call_put_array,
    buy_sell_array,
    index_array,
    quantity_array,
    expiry_dates
  ) {
    let row_list = [];
    let strike = "";

    const expiryDataMap = {};
    for (const expiryDate of expiry_dates) {
      const data = currentOptionsData[expiryDate];
      if (!data) {
        return [];
      }

      expiryDataMap[expiryDate] = data;
    }

    for (let index = 0; index < call_put_array.length; index++) {
      const expiryDate = expiry_dates[index];
      const expiryData = expiryDataMap[expiryDate];
      const side = call_put_array[index];
      let option;

      // For the first leg, determine strike
      if (index === 0 || strike === "") {
        const atmIndex = index_array[index];
        option =
          side === "Call"
            ? expiryData.options[atmIndex].call
            : expiryData.options[atmIndex].put;

        strike = option.strike;
      } else {
        // For subsequent legs, find the option with matching strike
        const matchingOption = expiryData.options.find((opt) => {
          const sideOption = side === "Call" ? opt.call : opt.put;
          return sideOption.strike === strike;
        });

        if (!matchingOption) {
          return [];
        }

        option = side === "Call" ? matchingOption.call : matchingOption.put;
      }

      row_list.push({
        buy_sell: buy_sell_array[index],
        code: option.code,
        expiryDate: option.expiry_date,
        instrumentName: option.instrument_name,
        lotSize: option.lot_size,
        side: side,
        tradingSymbol: option.trading_symbol,
        quantity: quantity_array[index],
        strike: option.strike,
        freeze_qty: option.freeze_qty,
        exchange: option.exchange,
        lot_multiplier: parseFloat(option.lot_size),
      });
    }

    return row_list;
  }
  function constructInstruments(
    call_put_array,
    buy_sell_array,
    index_array,
    quantity_array
  ) {
    let row_list = [];

    for (let index = 0; index < call_put_array.length; index++) {
      // Check if this should be a future instrument
      if (call_put_array[index] === "Fut") {
        // Create future instrument
        const futureInstrument = constructFutureInstrument(
          currentFuturesData,
          buy_sell_array[index],
          quantity_array[index]
        );
        if (futureInstrument) {
          row_list.push(futureInstrument);
        }
        continue;
      }

      let atmIndex = index_array[index];

      // Safety check for index
      if (atmIndex < 0) atmIndex = 0;
      if (atmIndex >= currentOptionsData[selectedExpiry].options?.length)
        atmIndex = currentOptionsData[selectedExpiry].options?.length - 1;

      const side = call_put_array[index];
      const option =
        side === "Call"
          ? currentOptionsData[selectedExpiry].options[atmIndex].call
          : currentOptionsData[selectedExpiry].options[atmIndex].put;

      row_list.push({
        buy_sell: buy_sell_array[index],
        code: option.code,
        expiryDate: option.expiry_date,
        instrumentName: option.instrument_name,
        lotSize: option.lot_size,
        side: side,
        tradingSymbol: option.trading_symbol,
        quantity: quantity_array[index],
        strike: option.strike,
        freeze_qty: option.freeze_qty,
        exchange: option.exchange,
        lot_multiplier: parseFloat(
          option.lot_size || currentOptionsData[selectedExpiry].lot_size
        ),
        isFuture: false,
      });
    }

    return row_list;
  }

  function returnAtmIndex() {
    let index = 0;
    const ltp = symbolData[selectedSymbol?.code]?.ltp || 0;

    if (
      currentOptionsData[selectedExpiry] &&
      currentOptionsData[selectedExpiry].options &&
      currentOptionsData[selectedExpiry].options?.length > 0
    ) {
      let optionData = currentOptionsData[selectedExpiry].options;
      let curr = parseFloat(optionData[0].strike);
      let diff = Math.abs(ltp - curr);

      for (let val = 0; val < optionData.length; val++) {
        const strike = parseFloat(optionData[val].strike);
        const newDiff = Math.abs(ltp - strike);

        if (newDiff < diff) {
          diff = newDiff;
          curr = strike;
          index = val;
        }
      }
    }

    return index;
  }

  function returnExpiryAtmIndex(expiry) {
    let index = 0;
    const ltp = symbolData[selectedSymbol?.code]?.ltp || 0;

    if (
      currentOptionsData[expiry] &&
      currentOptionsData[expiry].options &&
      currentOptionsData[expiry].options?.length > 0
    ) {
      let optionData = currentOptionsData[expiry].options;
      let curr = parseFloat(optionData[0].strike);
      let diff = Math.abs(ltp - curr);

      for (let val = 0; val < optionData.length; val++) {
        const strike = parseFloat(optionData[val].strike);
        const newDiff = Math.abs(ltp - strike);

        if (newDiff < diff) {
          diff = newDiff;
          curr = strike;
          index = val;
        }
      }
    }

    return index;
  }

  return { applyPreBuiltStrategy, applySavedStrategy, applyHedgeStrategy };
};
